import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../marketing/MarketingLayout';
import { IndustryData } from '../../data/industries';
import { ArrowRightIcon, CheckCircleIcon, LightBulbIcon, ShieldCheckIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import OptimizedImage from '../common/OptimizedImage';

interface IndustryPageTemplateProps {
  industry: IndustryData;
}

export default function IndustryPageTemplate({ industry }: IndustryPageTemplateProps) {
  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-blue-900 to-indigo-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-blue-900/50"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-sm mb-6">
              <LightBulbIcon className="h-4 w-4 mr-2" />
              Specialized Financial Strategies
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Financial Planning for {industry.name}
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              {industry.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-white text-blue-900 rounded-lg font-medium hover:bg-gray-100 transition-all">
                Get Your Custom Strategy
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
              <button className="inline-flex items-center px-6 py-3 bg-blue-800/50 backdrop-blur text-white rounded-lg font-medium hover:bg-blue-800/70 transition-all">
                Download Industry Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Income Range Bar */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <div>
                <p className="text-sm text-gray-600">Typical Income Range</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${industry.incomeRange.low.toLocaleString()} - ${industry.incomeRange.high.toLocaleString()}
                </p>
              </div>
              <div className="hidden md:block h-12 w-px bg-gray-300"></div>
              <div className="hidden md:flex items-center space-x-2">
                <span className="text-sm text-gray-600">Key Services:</span>
                {industry.commonServices.slice(0, 3).map((service, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    {service.split(' ').slice(0, 2).join(' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Opportunities */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Understanding Your Unique Financial Landscape
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">Common Challenges</h3>
                </div>
                <ul className="space-y-4">
                  {industry.challenges.map((challenge, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700">{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <LightBulbIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">Opportunities</h3>
                </div>
                <ul className="space-y-4">
                  {industry.opportunities.map((opportunity, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{opportunity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Stages Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Financial Planning Through Your Career Journey
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
              Your financial needs evolve throughout your career. We provide stage-appropriate strategies to maximize your success.
            </p>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>
              
              <div className="space-y-12">
                {industry.careerStages.map((stage, index) => (
                  <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline dot */}
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full"></div>
                    
                    <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{stage.stage}</h3>
                        <p className="text-gray-600 mb-4">{stage.description}</p>
                        <div className="space-y-2">
                          {stage.financialPriorities.map((priority, pIndex) => (
                            <div key={pIndex} className="flex items-center text-sm text-gray-700">
                              <ChartBarIcon className="h-4 w-4 text-blue-600 mr-2" />
                              {priority}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Tailored Financial Services for {industry.name}
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
              Our comprehensive suite of services addresses every aspect of your financial life.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.commonServices.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-lg transition-all group">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <ShieldCheckIcon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{service}</h3>
                  <p className="text-gray-600 mb-4">
                    Customized strategies designed specifically for the unique needs and opportunities in your industry.
                  </p>
                  <Link to="/services" className="text-blue-600 font-medium hover:text-blue-700 inline-flex items-center group-hover:translate-x-1 transition-transform">
                    Learn More <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tax Considerations */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Tax Optimization Strategies for {industry.name}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.taxConsiderations.map((tax, index) => (
                <div key={index} className="bg-white/10 backdrop-blur rounded-lg p-6 border border-white/20">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-white font-semibold">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{tax}</h3>
                      <p className="text-blue-100 text-sm">
                        Strategic planning to minimize tax burden and maximize wealth accumulation.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-xl mb-6">
                The average {industry.name.toLowerCase()} can save ${Math.round(industry.incomeRange.high * 0.15).toLocaleString()} annually with proper tax planning.
              </p>
              <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-white text-blue-900 rounded-lg font-semibold hover:bg-gray-100 transition-all">
                Calculate Your Tax Savings
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Success Stories from {industry.name}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">$2.3M</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Savings Achieved</h3>
                <p className="text-gray-600 mb-4">
                  Medical professional saved over $2.3M through strategic professional corporation planning over 10 years.
                </p>
                <Link to="/case-studies" className="text-blue-600 font-medium hover:text-blue-700">
                  Read Full Story →
                </Link>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                <div className="text-4xl font-bold text-green-600 mb-2">15 Years</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Early Retirement</h3>
                <p className="text-gray-600 mb-4">
                  Tech executive achieved financial independence 15 years ahead of schedule with equity optimization.
                </p>
                <Link to="/case-studies" className="text-blue-600 font-medium hover:text-blue-700">
                  Read Full Story →
                </Link>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Estate Protected</h3>
                <p className="text-gray-600 mb-4">
                  Business owner secured complete estate protection while minimizing tax burden for heirs.
                </p>
                <Link to="/case-studies" className="text-blue-600 font-medium hover:text-blue-700">
                  Read Full Story →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Essential Resources for {industry.name}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-lg transition-all group">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Tax Planning Guide</h3>
                <p className="text-sm text-gray-600 mb-4">2024 comprehensive tax strategies</p>
                <button className="text-blue-600 font-medium hover:text-blue-700">Download PDF</button>
              </div>

              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-lg transition-all group">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Income Calculator</h3>
                <p className="text-sm text-gray-600 mb-4">Optimize your compensation</p>
                <button className="text-blue-600 font-medium hover:text-blue-700">Try Now</button>
              </div>

              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-lg transition-all group">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                  <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Planning Timeline</h3>
                <p className="text-sm text-gray-600 mb-4">Key milestones checklist</p>
                <button className="text-blue-600 font-medium hover:text-blue-700">View Timeline</button>
              </div>

              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-lg transition-all group">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                  <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Industry Report</h3>
                <p className="text-sm text-gray-600 mb-4">Latest trends & insights</p>
                <button className="text-blue-600 font-medium hover:text-blue-700">Read Report</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Optimize Your Financial Strategy?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of {industry.name.toLowerCase()} who trust PCM Financial for their wealth management needs.
            </p>
            <div className="bg-white/10 backdrop-blur rounded-xl p-8 max-w-2xl mx-auto mb-8">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div>
                  <div className="text-3xl font-bold text-white mb-1">98%</div>
                  <p className="text-gray-300 text-sm">Client Satisfaction</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">$500M+</div>
                  <p className="text-gray-300 text-sm">Assets Managed</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white mb-1">25+</div>
                  <p className="text-gray-300 text-sm">Years Experience</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all">
                Get Your Custom Plan
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </Link>
              <button className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all">
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Watch Success Stories
              </button>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}